import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { SafeAreaView } from 'react-native-safe-area-context';
import { onboardingService, TaskPriority as ApiTaskPriority } from '../../services/onboarding';
import { analytics } from '../../services/analytics/analytics';
import { AnalyticsEvent } from '../../services/analytics/events';

import { createLogger } from '../../utils/logger';
const logger = createLogger('AIOnboardingPriorities');

interface TaskPriority {
  taskKey: string;
  displayName: string;
  priorityTier: 'critical' | 'high' | 'medium' | 'low';
  priorityScore: number;
  reasoning: string;
}

// Map task keys to human-readable display names
const taskDisplayNames: Record<string, string> = {
  // Personal tasks
  personal_info: 'Personal Information',
  children_info: 'Children Information',
  spouse_info: 'Spouse/Partner Information',
  pet_info: 'Pet Information',
  employment: 'Employment Information',

  // Medical tasks
  medication: 'Medications',
  medications: 'Medications',
  primary_physician: 'Primary Physician',
  primary_physician_info: 'Primary Physician',
  specialty_physician: 'Specialty Physicians',
  specialty_physician_info: 'Specialty Physicians',
  medical_history: 'Medical History',
  caregiver: 'Caregiver Information',
  caregiver_info: 'Caregiver Information',
  medical_equipment: 'Medical Equipment',
  organ_donor: 'Organ Donor Preferences',
  treatment: 'Treatments',
  treatments: 'Treatments',

  // Financial tasks
  accountant: 'Accountant Information',
  attorney: 'Attorney Information',
  investment_advisor: 'Investment Advisor',
  insurance_agent: 'Insurance Agent',
  tax_advisor: 'Tax Advisor',
  credit_card: 'Credit Cards',
  banking: 'Banking Information',
  investments: 'Investments',
  crypto: 'Cryptocurrency',
  loans: 'Loans',

  // Home tasks
  homeowners_insurance: 'Homeowners Insurance',
  home_ownership: 'Home Ownership',
  home_inventory: 'Home Inventory',

  // Utilities
  cellphone_provider: 'Cellphone Provider',
  gas_provider: 'Gas Utility',
  electric_provider: 'Electric Utility',
  waste_provider: 'Waste Service',
  water_sewer_provider: 'Water/Sewer',
  streaming_service: 'Streaming Services',

  // Services
  lawn_service: 'Lawn Service',
  pool_service: 'Pool Service',
  pest_control: 'Pest Control',
  gym_membership: 'Gym Membership',
  home_security: 'Home Security',

  // Funeral tasks
  funeral_home: 'Funeral Home',
  casket_urn: 'Casket/Urn',
  funeral_instructions: 'Funeral Instructions',
  body_arrangements: 'Body Arrangements',
  memorial_service: 'Memorial Service',
  tribute_donations: 'Tribute Donations',
  officiant: 'Officiant',
  letter_to_loved_ones: 'Letter to Loved Ones',
  prayers_readings: 'Prayers & Readings',
  food_preferences: 'Food Preferences',
  music: 'Music',
  pallbearers: 'Pallbearers',
  traditions_rituals: 'Traditions & Rituals',
  wake_details: 'Wake Details',
  eulogy: 'Eulogy',
  obituary: 'Obituary',
  obituary_details: 'Obituary',
  burial_plot: 'Burial Plot',
  burial_plot_info: 'Burial Plot',
  headstone: 'Headstone',
  headstone_details: 'Headstone',

  // End of life preferences
  advance_directive: 'Advance Directive',
  ltc_preferences: 'Long-Term Care Preferences',
  dnr_preferences: 'DNR Preferences',
  breathing_tube: 'Breathing Tube Preferences',
  feeding_tube: 'Feeding Tube Preferences',
  medical_poa: 'Medical Power of Attorney',
  medical_poa_preferences: 'Medical Power of Attorney',
  pet_care: 'Pet Care Arrangements',
  pet_care_preferences: 'Pet Care Arrangements',
  living_trust: 'Living Trust',
  living_trust_preferences: 'Living Trust',
};

export const AIOnboardingPrioritiesScreen = ({ navigation }: any) => {
  const [priorities, setPriorities] = useState<TaskPriority[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    analytics.track(AnalyticsEvent.AiOnboardingPrioritiesShown);
    loadPriorities();
  }, []);

  const loadPriorities = async () => {
    const defaultPriorities: TaskPriority[] = [
      {
        taskKey: 'personal_info',
        displayName: 'Personal Information',
        priorityTier: 'high',
        priorityScore: 65,
        reasoning: 'Basic information for your profile',
      },
      {
        taskKey: 'medical_history',
        displayName: 'Medical History',
        priorityTier: 'medium',
        priorityScore: 50,
        reasoning: 'Important health information to document',
      },
      {
        taskKey: 'primary_physician_info',
        displayName: 'Primary Physician',
        priorityTier: 'medium',
        priorityScore: 45,
        reasoning: 'Key healthcare contact information',
      },
      {
        taskKey: 'advance_directive',
        displayName: 'Advance Directive',
        priorityTier: 'medium',
        priorityScore: 40,
        reasoning: 'Important legal document for healthcare decisions',
      },
      {
        taskKey: 'spouse_info',
        displayName: 'Spouse/Partner Information',
        priorityTier: 'medium',
        priorityScore: 40,
        reasoning: 'Family information for your profile',
      },
    ];

    try {
      // Call backend API to get priorities
      const apiPriorities = await onboardingService.getPriorities();

      // If API returns empty array, use defaults
      if (!apiPriorities || apiPriorities.length === 0) {
        setPriorities(defaultPriorities);
        setLoading(false);
        return;
      }

      // Transform API response to include display names
      const transformedPriorities: TaskPriority[] = apiPriorities
        .map((p: ApiTaskPriority) => ({
          taskKey: p.taskKey,
          displayName: taskDisplayNames[p.taskKey] || formatTaskKey(p.taskKey),
          priorityTier: p.priorityTier,
          priorityScore: p.priorityScore,
          reasoning: p.reasoning || 'Based on your onboarding responses',
        }))
        .sort((a, b) => b.priorityScore - a.priorityScore); // Sort by score descending

      setPriorities(transformedPriorities);
      setLoading(false);
    } catch (error: any) {
      logger.error('Failed to load priorities:', error);
      setLoading(false);
      // Use default priorities on error
      setPriorities(defaultPriorities);
    }
  };

  // Helper function to format task keys as display names if not in mapping
  const formatTaskKey = (taskKey: string): string => {
    return taskKey
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getPriorityColor = (tier: string) => {
    switch (tier) {
      case 'critical':
        return '#E85C5C';
      case 'high':
        return '#F2994A';
      case 'medium':
        return '#F2C94C';
      case 'low':
        return '#6FCF97';
      default:
        return '#999';
    }
  };

  const getPriorityIcon = (tier: string) => {
    switch (tier) {
      case 'critical':
        return '🔴';
      case 'high':
        return '🟠';
      case 'medium':
        return '🟡';
      case 'low':
        return '🟢';
      default:
        return '⚪';
    }
  };

  // Map task keys to their navigator and screen names
  const getTaskNavigation = (taskKey: string): { navigator: string; screen: string } | null => {
    const taskNavMap: Record<string, { navigator: string; screen: string }> = {
      // Personal tasks -> Personal navigator
      personal_info: { navigator: 'Personal', screen: 'PersonalInfoIntro' },
      children_info: { navigator: 'Personal', screen: 'ChildrenInfoIntro' },
      spouse_info: { navigator: 'Personal', screen: 'SpouseInfoIntro' },
      pet_info: { navigator: 'Personal', screen: 'PetIntro' },
      employment: { navigator: 'Personal', screen: 'EmploymentInfoIntro' },

      // Medical tasks -> Medical navigator
      medication: { navigator: 'Medical', screen: 'MedicationsIntro' },
      medications: { navigator: 'Medical', screen: 'MedicationsIntro' },
      primary_physician_info: { navigator: 'Medical', screen: 'PrimaryPhysicianIntro' },
      primary_physician: { navigator: 'Medical', screen: 'PrimaryPhysicianIntro' },
      specialty_physician_info: { navigator: 'Medical', screen: 'SpecialtyPhysicianIntro' },
      specialty_physician: { navigator: 'Medical', screen: 'SpecialtyPhysicianIntro' },
      medical_history: { navigator: 'Medical', screen: 'MedicalHistoryIntro' },
      caregiver_info: { navigator: 'Medical', screen: 'CaregiverIntro' },
      caregiver: { navigator: 'Medical', screen: 'CaregiverIntro' },
      medical_equipment: { navigator: 'Medical', screen: 'MedicalEquipmentIntro' },
      organ_donor: { navigator: 'Medical', screen: 'OrganDonorIntro' },
      treatment: { navigator: 'Medical', screen: 'TreatmentsIntro' },
      treatments: { navigator: 'Medical', screen: 'TreatmentsIntro' },

      // Financial tasks -> Finance navigator
      accountant: { navigator: 'Finance', screen: 'AccountantInfoIntro' },
      attorney: { navigator: 'Finance', screen: 'AttorneyInfoIntro' },
      investment_advisor: { navigator: 'Finance', screen: 'InvestmentAdvisorInfoIntro' },
      insurance_agent: { navigator: 'Finance', screen: 'InsuranceAgentInfoIntro' },
      tax_advisor: { navigator: 'Finance', screen: 'TaxAdvisorInfoIntro' },
      credit_card: { navigator: 'Finance', screen: 'CreditCardInfoIntro' },
      banking: { navigator: 'Finance', screen: 'BankingInfoIntro' },
      investments: { navigator: 'Finance', screen: 'InvestmentsInfoIntro' },
      crypto: { navigator: 'Finance', screen: 'CryptoInfoIntro' },
      loans: { navigator: 'Finance', screen: 'LoanInfoIntro' },

      // Property tasks -> Property navigator
      homeowners_insurance: { navigator: 'Property', screen: 'HomeownersInsuranceIntro' },
      home_ownership: { navigator: 'Property', screen: 'HomeOwnershipIntro' },
      home_inventory: { navigator: 'Property', screen: 'HomeInventoryIntro' },
      cellphone_provider: { navigator: 'Property', screen: 'CellphoneProviderIntro' },
      gas_provider: { navigator: 'Property', screen: 'GasUtilityProviderIntro' },
      electric_provider: { navigator: 'Property', screen: 'ElectricProviderIntro' },
      waste_provider: { navigator: 'Property', screen: 'WasteProviderIntro' },
      water_sewer_provider: { navigator: 'Property', screen: 'WaterSewerProviderIntro' },
      streaming_service: { navigator: 'Property', screen: 'StreamingServiceProviderIntro' },
      lawn_service: { navigator: 'Property', screen: 'LawnServiceProviderIntro' },
      pool_service: { navigator: 'Property', screen: 'PoolServiceProviderIntro' },
      pest_control: { navigator: 'Property', screen: 'PestControlProviderIntro' },
      gym_membership: { navigator: 'Property', screen: 'GymMembershipIntro' },
      home_security: { navigator: 'Property', screen: 'HomeSecurityIntro' },

      // Final Care tasks -> FinalCare navigator
      advance_directive: { navigator: 'FinalCare', screen: 'AdvanceDirectiveIntro' },
      ltc_preferences: { navigator: 'FinalCare', screen: 'LTCPreferencesIntro' },
      dnr_preferences: { navigator: 'FinalCare', screen: 'DNRPreferencesIntro' },
      breathing_tube: { navigator: 'FinalCare', screen: 'BreathingTubePreferencesIntro' },
      feeding_tube: { navigator: 'FinalCare', screen: 'FeedingTubePreferencesIntro' },
      medical_poa_preferences: { navigator: 'FinalCare', screen: 'MedicalPOAPreferencesIntro' },
      medical_poa: { navigator: 'FinalCare', screen: 'MedicalPOAPreferencesIntro' },
      pet_care_preferences: { navigator: 'FinalCare', screen: 'PetCarePreferencesIntro' },
      pet_care: { navigator: 'FinalCare', screen: 'PetCarePreferencesIntro' },
      living_trust_preferences: { navigator: 'FinalCare', screen: 'LivingTrustPreferencesIntro' },
      living_trust: { navigator: 'FinalCare', screen: 'LivingTrustPreferencesIntro' },

      // Wishes tasks -> Wishes navigator
      funeral_home: { navigator: 'Wishes', screen: 'FuneralHomeIntro' },
      casket_urn: { navigator: 'Wishes', screen: 'CasketUrnIntro' },
      funeral_instructions: { navigator: 'Wishes', screen: 'FuneralInstructionsIntro' },
      body_arrangements: { navigator: 'Wishes', screen: 'BodyArrangementsIntro' },
      memorial_service: { navigator: 'Wishes', screen: 'MemorialServiceIntro' },
      tribute_donations: { navigator: 'Wishes', screen: 'TributeDonationsIntro' },
      officiant: { navigator: 'Wishes', screen: 'OfficiantIntro' },
      letter_to_loved_ones: { navigator: 'Wishes', screen: 'LetterToLovedOnesIntro' },
      prayers_readings: { navigator: 'Wishes', screen: 'PrayersReadingsIntro' },
      food_preferences: { navigator: 'Wishes', screen: 'FoodPreferencesIntro' },
      music: { navigator: 'Wishes', screen: 'MusicIntro' },
      pallbearers: { navigator: 'Wishes', screen: 'PallBearerIntro' },
      traditions_rituals: { navigator: 'Wishes', screen: 'TraditionsRitualsIntro' },
      wake_details: { navigator: 'Wishes', screen: 'WakeDetailsIntro' },
      eulogy: { navigator: 'Wishes', screen: 'EulogyIntro' },
      obituary_details: { navigator: 'Wishes', screen: 'ObituaryDetailsIntro' },
      obituary: { navigator: 'Wishes', screen: 'ObituaryDetailsIntro' },
      burial_plot_info: { navigator: 'Wishes', screen: 'BurialPlotIntro' },
      burial_plot: { navigator: 'Wishes', screen: 'BurialPlotIntro' },
      headstone_details: { navigator: 'Wishes', screen: 'HeadstoneDetailsIntro' },
      headstone: { navigator: 'Wishes', screen: 'HeadstoneDetailsIntro' },
    };

    return taskNavMap[taskKey] || null;
  };

  const handleTaskPress = (taskKey: string) => {
    const navInfo = getTaskNavigation(taskKey);

    if (navInfo) {
      // Navigate to the appropriate category navigator, then to the specific screen
      navigation.navigate(navInfo.navigator as any, {
        screen: navInfo.screen,
      });
    } else {
      Alert.alert(
        'Coming Soon',
        `The ${taskDisplayNames[taskKey] || taskKey} task screen is being developed. Please check back later!`,
        [{ text: 'OK' }]
      );
    }
  };

  const handleDone = async () => {
    try {
      // Mark onboarding as complete
      await onboardingService.completeOnboarding();
    } catch (error) {
      logger.error('Failed to mark onboarding complete:', error);
      // Continue anyway - onboarding is effectively done
    }
    // Navigate to Dashboard (which shows the main home screen with all checklists)
    navigation.reset({
      index: 0,
      routes: [{ name: 'Dashboard' }],
    });
  };

  const displayedPriorities = showAll
    ? priorities
    : priorities.slice(0, 3);

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>
            Analyzing your responses...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <TouchableOpacity onPress={handleDone}>
          <Text style={styles.doneText}>Done</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        {/* Title */}
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Your Personalized Task Priorities</Text>
          <Text style={styles.subtitle}>
            Based on our conversation, here's what's most important for you
            right now:
          </Text>
        </View>

        {/* Priority Cards */}
        <View style={styles.prioritiesContainer}>
          {displayedPriorities.map((priority, index) => (
            <TouchableOpacity
              key={priority.taskKey}
              style={[
                styles.priorityCard,
                { borderLeftColor: getPriorityColor(priority.priorityTier) },
              ]}
              onPress={() => handleTaskPress(priority.taskKey)}
              activeOpacity={0.8}
            >
              <View style={styles.cardHeader}>
                <View style={styles.cardTitleRow}>
                  <Text style={styles.priorityIcon}>
                    {getPriorityIcon(priority.priorityTier)}
                  </Text>
                  <Text style={styles.cardTitle}>{priority.displayName}</Text>
                </View>
                <Text style={styles.cardArrow}>{'>'}</Text>
              </View>

              <View style={styles.priorityBadge}>
                <Text
                  style={[
                    styles.priorityBadgeText,
                    { color: getPriorityColor(priority.priorityTier) },
                  ]}
                >
                  {priority.priorityTier.toUpperCase()} PRIORITY
                </Text>
              </View>

              <Text style={styles.reasoning}>{priority.reasoning}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Show More/Less Button */}
        {priorities.length > 3 && (
          <TouchableOpacity
            style={styles.showMoreButton}
            onPress={() => setShowAll(!showAll)}
          >
            <Text style={styles.showMoreText}>
              {showAll ? '▲ Show Less' : `▼ Show All ${priorities.length} Tasks`}
            </Text>
          </TouchableOpacity>
        )}

        {/* CTA Button */}
        <TouchableOpacity style={styles.startButton} onPress={handleDone}>
          <Text style={styles.startButtonText}>Get Started</Text>
        </TouchableOpacity>

        <Text style={styles.noteText}>
          You can adjust these priorities anytime from your task list
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#1C2C4F',
  },
  doneText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: ms(16),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#666',
  },
  content: {
    paddingHorizontal: ms(24),
    paddingTop: ms(24),
    paddingBottom: ms(40),
  },
  titleContainer: {
    marginBottom: ms(32),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(12),
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#666',
    lineHeight: ms(24, 0.3),
  },
  prioritiesContainer: {
    gap: ms(16),
    marginBottom: ms(16),
  },
  priorityCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(16),
    padding: ms(20),
    borderLeftWidth: ms(4),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(12),
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(8),
    flex: 1,
  },
  priorityIcon: {
    fontSize: ms(20, 0.3),
  },
  cardTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    flex: 1,
  },
  cardArrow: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  priorityBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: ms(12),
    paddingVertical: ms(4),
    borderRadius: ms(12),
    backgroundColor: '#F3F6F8',
    marginBottom: ms(12),
  },
  priorityBadgeText: {
    fontSize: ms(11, 0.3),
    fontWeight: '700',
    letterSpacing: ms(0.5),
  },
  reasoning: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
  },
  showMoreButton: {
    alignItems: 'center',
    paddingVertical: ms(16),
  },
  showMoreText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  startButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(16),
    alignItems: 'center',
    marginTop: ms(24),
    marginBottom: ms(16),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  startButtonText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  noteText: {
    fontSize: ms(14, 0.3),
    color: '#999',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
  },
});
